<template>
  <div class="referrals-view">
   <vs-tabs v-model="currentTab">
    <vs-tab label="Referrals" class="nofilterimags px-0">

    <!-- ============ CHECK A CODE console ============ -->
    <vx-card title="Check a reward code" class="mb-base">
      <p class="mb-4 text-sm" style="color:#888">
        When a friend or customer shows you a code, type it here to look it up and confirm a new customer.
      </p>
      <div class="flex mb-4">
        <vs-input class="w-full mr-2" v-model="lookupCode" placeholder="e.g. BRK7Q2"
          @keyup.enter="runLookup" style="text-transform:uppercase" />
        <vs-button :disabled="lookingUp" @click="runLookup">
          <span v-if="lookingUp" class="btn-spin mr-2"></span>Look up
        </vs-button>
      </div>

      <div v-if="lookupDone && !lookupResult.found" class="text-sm" style="color:#c0392b">
        No reward code matches that. Double-check the spelling.
      </div>

      <div v-if="lookupResult.found" class="lookup-result">
        <vs-divider />
        <p class="mb-1">
          <strong>{{ lookupResult.kind === 'referrer' ? 'Referrer reward code' : (lookupResult.kind === 'group' ? 'Group voucher code' : 'Friend welcome code') }}</strong>
          — <span :style="{color: lookupResult.used ? '#999' : '#27ae60'}">
            {{ lookupResult.used ? 'already used' : 'not yet used' }}
          </span>
        </p>
        <!-- GROUP voucher: one code covers the whole group — show organizer + members + fill state -->
        <p class="text-sm mb-1" v-if="lookupResult.kind === 'group' && lookupResult.referral">
          Group: {{ lookupResult.referral.organizerName || '—' }}'s group — {{ groupFillText(lookupResult.referral) }} joined<br>
          <template v-for="(m, mi) in groupMembers(lookupResult.referral)">
            <span :key="'lkm' + mi" style="display:block;padding-left:12px">
              • {{ m.name || '—' }}<span style="color:#999"> {{ m.email }}</span><span v-if="m.phone" style="color:#999"> · {{ m.phone }}</span>
            </span>
          </template>
          Referrer: {{ lookupResult.referral.referrerName || lookupResult.referral.referrerEmail || '—' }}<br>
          Lead: <span v-html="leadStatusPill(deriveLeadStatus(lookupResult.referral))"></span>
        </p>
        <p class="text-sm mb-1" v-else-if="lookupResult.referral">
          Friend: {{ lookupResult.referral.friendName || lookupResult.referral.friendEmail || '—' }}<br>
          <template v-if="lookupResult.referral.friendPhone">Phone: {{ lookupResult.referral.friendPhone }}<br></template>
          Referrer: {{ lookupResult.referral.referrerName || lookupResult.referral.referrerEmail || '—' }}<br>
          Lead: <span v-html="leadStatusPill(deriveLeadStatus(lookupResult.referral))"></span>
        </p>

        <div class="mt-3">
          <vs-button v-if="(lookupResult.kind === 'friend' || lookupResult.kind === 'group') && lookupResult.referral && lookupResult.referral.status === 'invited'"
            color="success" class="mr-2 mb-2"
            :disabled="processingId === lookupResult.referral.referralId"
            @click="confirmFromLookup(lookupResult.referral)">
            <span v-if="processingId === lookupResult.referral.referralId && processingAction === 'confirm'" class="btn-spin mr-2"></span>{{ lookupResult.kind === 'group' ? 'Confirm this group' : 'Confirm this is a new customer' }}
          </vs-button>
          <vs-button v-if="!lookupResult.used" color="primary" type="border" class="mb-2"
            :disabled="markingUsed"
            @click="markUsed(lookupResult.code)">
            <span v-if="markingUsed" class="btn-spin mr-2"></span>Mark code as used
          </vs-button>
        </div>
      </div>
    </vx-card>

    <!-- ============ REFERRALS TABLE ============ -->
    <vx-card title="Referrals">
      <div class="flex flex-wrap items-center mb-4">
        <vs-button size="small" :type="statusFilter === 'all' ? 'filled' : 'border'" class="mr-2 mb-2" @click="statusFilter = 'all'">All ({{ referrals.length }})</vs-button>
        <vs-button size="small" :type="statusFilter === 'invited' ? 'filled' : 'border'" class="mr-2 mb-2" @click="statusFilter = 'invited'">Invited ({{ countBy('invited') }})</vs-button>
        <vs-button size="small" :type="statusFilter === 'redeemed' ? 'filled' : 'border'" class="mr-2 mb-2" @click="statusFilter = 'redeemed'">Redeemed ({{ countBy('redeemed') }})</vs-button>
        <vs-button size="small" :type="statusFilter === 'rewarded' ? 'filled' : 'border'" class="mr-2 mb-2" @click="statusFilter = 'rewarded'">Rewarded ({{ countBy('rewarded') }})</vs-button>
        <vs-button size="small" :type="statusFilter === 'rejected' ? 'filled' : 'border'" class="mr-2 mb-2" @click="statusFilter = 'rejected'">Rejected ({{ countBy('rejected') }})</vs-button>
        <vs-button size="small" type="flat" class="mb-2" :disabled="loadingReferrals" @click="loadReferrals">Refresh</vs-button>
      </div>

      <div v-if="loadingReferrals" class="text-center py-10">
        <div class="w-8 h-8 pt_loading inline-block"></div>
        <p class="mt-2 text-light">Loading referrals...</p>
      </div>
      <div v-else-if="filteredReferrals.length === 0" class="text-sm" style="color:#888">
        No referrals yet. Add one in the
        <a href="#" @click.prevent="currentTab = 1" class="font-semibold">Add by hand</a> tab,
        or turn on the survey step to capture them automatically.
      </div>

      <vs-table v-else :data="filteredReferrals" stripe>
        <template slot="thead">
          <vs-th>Friend</vs-th>
          <vs-th>Referrer</vs-th>
          <vs-th>Lead status</vs-th>
          <vs-th>Codes</vs-th>
          <vs-th>Source</vs-th>
          <vs-th>Reward</vs-th>
        </template>
        <template slot-scope="{ data }">
          <vs-tr :key="i" v-for="(tr, i) in data">
            <vs-td :data="data[i].friendName">
              <!-- GROUP rows: one row = the whole group; members expand underneath.
                   Everything here is origin==='group'-guarded — old docs have no members. -->
              <template v-if="data[i].origin === 'group'">
                {{ data[i].organizerName || '—' }}'s group
                <vs-chip color="primary" class="ml-1">{{ groupFillText(data[i]) }}</vs-chip>
                <vs-chip v-if="data[i].duplicateFlag" color="warning" class="ml-1">possible repeat</vs-chip>
                <br>
                <a href="#" class="text-sm font-semibold" @click.prevent="toggleGroupExpand(data[i].referralId)">
                  {{ expandedGroups[data[i].referralId] ? 'Hide members' : 'Show members' }}
                </a>
                <div v-if="expandedGroups[data[i].referralId]" class="mt-2">
                  <div v-for="(m, mi) in groupMembers(data[i])" :key="mi" class="text-sm mb-1">
                    {{ m.name || '—' }}<br>
                    <span style="color:#999">{{ m.email }}</span>
                    <span v-if="m.phone" style="color:#999"> · {{ m.phone }}</span>
                    <vs-chip v-if="m.duplicateFlag" color="warning" class="ml-1">possible repeat</vs-chip>
                  </div>
                </div>
              </template>
              <template v-else>
                {{ data[i].friendName || '—' }}<br>
                <span class="text-sm" style="color:#999">{{ data[i].friendEmail }}</span>
                <br v-if="data[i].friendPhone">
                <span class="text-sm" style="color:#999" v-if="data[i].friendPhone">{{ data[i].friendPhone }}</span>
                <vs-chip v-if="data[i].duplicateFlag" color="warning" class="ml-1">possible repeat</vs-chip>
              </template>
            </vs-td>
            <vs-td :data="data[i].referrerName">
              {{ data[i].referrerName || '—' }}<br>
              <span class="text-sm" style="color:#999">{{ data[i].referrerEmail }}</span>
            </vs-td>
            <vs-td :data="data[i].status">
              <!-- terminal lead states are irreversible (fired reward / rejection) → read-only pill -->
              <span v-if="deriveLeadStatus(data[i]) === 'converted' || deriveLeadStatus(data[i]) === 'not_interested'"
                v-html="leadStatusPill(deriveLeadStatus(data[i]))"></span>
              <select v-else
                class="leadstatus-select"
                :value="deriveLeadStatus(data[i])"
                :disabled="processingId === data[i].referralId"
                @change="onLeadStatusChange(data[i], $event.target.value, $event)">
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted</option>
                <option value="not_interested">Not interested</option>
              </select>
              <span v-if="processingId === data[i].referralId" class="btn-spin ml-2"></span>
            </vs-td>
            <vs-td :data="data[i].friendCode">
              <!-- the group voucher rides friendCode (one code for the whole group) — only the
                   display label changes for group rows, never the field -->
              <span class="text-sm" v-if="data[i].friendCode">
                {{ data[i].origin === 'group' ? 'Group voucher' : 'Friend' }}: <strong>{{ data[i].friendCode }}</strong>
                <span :style="{color: data[i].friendCodeUsed ? '#999' : '#27ae60'}">({{ data[i].friendCodeUsed ? 'used' : 'unused' }})</span>
              </span>
              <!-- reward-less referrals carry no welcome code; ON-mode groups have no voucher until release -->
              <span class="text-sm" v-else-if="data[i].origin === 'group'" style="color:#999">no voucher yet</span>
              <span class="text-sm" v-else style="color:#999">—</span>
              <br v-if="data[i].referrerCode">
              <span class="text-sm" v-if="data[i].referrerCode">
                Referrer: <strong>{{ data[i].referrerCode }}</strong>
                <span :style="{color: data[i].referrerCodeUsed ? '#999' : '#27ae60'}">({{ data[i].referrerCodeUsed ? 'used' : 'unused' }})</span>
              </span>
            </vs-td>
            <vs-td :data="data[i].origin"><span v-html="sourceBadge(data[i].origin)"></span></vs-td>
            <vs-td :data="data[i].status">
              <span v-if="data[i].status === 'rewarded'" class="text-sm" style="color:#1e7e34">✓ reward sent</span>
              <span v-else-if="data[i].status === 'redeemed'" class="text-sm" style="color:#b26a00">redeemed</span>
              <span v-else class="text-sm" style="color:#bbb">—</span>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>

    </vs-tab>
    <vs-tab label="Add by hand" class="nofilterimags px-0">
      <AddReferralByHand @referraladded="loadReferrals" />
    </vs-tab>
    <vs-tab label="Referral links" class="nofilterimags px-0">
      <CreateReferralLink />
    </vs-tab>
   </vs-tabs>
  </div>
</template>

<script>
import referralsMixin from './referralsMixin.js'
import AddReferralByHand from './AddReferralByHand.vue'
import CreateReferralLink from './CreateReferralLink.vue'

export default {
  mixins: [referralsMixin],
  components: { AddReferralByHand, CreateReferralLink },
  data () {
    return {
      currentTab: 0,
      lookupCode: '',
      lookingUp: false,
      lookupDone: false,
      lookupResult: { found: false },
      markingUsed: false,

      referrals: [],
      loadingReferrals: false,
      statusFilter: 'all',

      expandedGroups: {},      // referralId -> true while a group row's member list is open

      processingId: null,      // referralId currently being confirmed/rejected
      processingAction: null   // 'confirm' | 'reject'
    }
  },
  computed: {
    filteredReferrals () {
      if (this.statusFilter === 'all') return this.referrals
      return this.referrals.filter(r => r.status === this.statusFilter)
    }
  },
  methods: {
    countBy (status) {
      return this.referrals.filter(r => r.status === status).length
    },

    // ---- GROUP REFERRALS Phase 5 — group-row helpers (always origin==='group'-guarded
    // by the caller; old standard docs have no members/groupSize fields) ----
    groupMembers (referral) {
      return (referral && Array.isArray(referral.members)) ? referral.members : []
    },
    groupFillText (referral) {
      const joined = this.groupMembers(referral).length
      return `${joined} of ${referral.groupSize || '?'}`
    },
    toggleGroupExpand (referralId) {
      this.$set(this.expandedGroups, referralId, !this.expandedGroups[referralId])
    },
    // Fill-state confirm prompt for group rows (adopted default: partial confirm allowed)
    groupConfirmPrompt (referral) {
      const joined = this.groupMembers(referral).length
      const size = referral.groupSize || joined
      if (joined < size) return `Only ${joined} of ${size} joined — confirm and redeem this group anyway?`
      return `Confirm and redeem this group (${joined} of ${size} joined)? This sends the referrer their reward if one is set up.`
    },

    loadReferrals () {
      this.loadingReferrals = true
      return this.$store.dispatch('dash/FETCH_REFERRALS').then((res) => {
        this.loadingReferrals = false
        this.referrals = (res && res.referrals) || []
      }).catch((e) => {
        this.loadingReferrals = false
        this.notifyError(this.referralErrorText(e, 'Could not load your referrals'))
      })
    },

    runLookup () {
      const code = (this.lookupCode || '').trim()
      if (!code) return
      this.lookingUp = true
      this.lookupDone = false
      this.$store.dispatch('dash/LOOKUP_REFERRAL_CODE', { code }).then((res) => {
        this.lookingUp = false
        this.lookupDone = true
        this.lookupResult = res || { found: false }
      }).catch((e) => {
        this.lookingUp = false
        this.lookupDone = true
        this.lookupResult = { found: false }
        this.notifyError(this.referralErrorText(e, 'Could not look up that code'))
      })
    },

    markUsed (code) {
      this.markingUsed = true
      this.$store.dispatch('dash/MARK_REFERRAL_CODE_USED', { code, used: true }).then(() => {
        this.markingUsed = false
        this.notifySuccess('Marked as used')
        if (this.lookupResult && this.lookupResult.code === code) this.lookupResult.used = true
        this.loadReferrals()
      }).catch((e) => {
        this.markingUsed = false
        this.notifyError(this.referralErrorText(e, 'Could not mark the code as used'))
      })
    },

    confirmFromLookup (referral) {
      this.confirmReferral(referral, 'confirm')
    },

    // PHASE 3.1 — the lead-status dropdown drives the reward machine underneath.
    // "Converted" fires the referrer reward (confirm); "Not interested" rejects.
    onLeadStatusChange (referral, newStatus, ev) {
      const current = this.deriveLeadStatus(referral)
      if (newStatus === current) return
      if (this.processingId) { if (ev && ev.target) ev.target.value = current; return }

      // irreversible: picking "Converted" emails the referrer their reward — confirm first.
      // Group rows get the fill state instead (partial-group confirm is allowed by design).
      if (newStatus === 'converted') {
        const prompt = referral.origin === 'group'
          ? this.groupConfirmPrompt(referral)
          : 'This sends the referrer their reward — continue?'
        if (!window.confirm(prompt)) {
          if (ev && ev.target) ev.target.value = current // revert the native select
          return
        }
      }

      this.processingId = referral.referralId
      this.$store.dispatch('dash/SET_REFERRAL_LEAD_STATUS', {
        referralId: referral.referralId,
        leadStatus: newStatus
      }).then((res) => {
        this.processingId = null
        if (newStatus === 'converted') {
          if (res && res.rewardReason === 'emailfailed') {
            this.notifyWarning(`Converted, but the reward email could not be sent.${res.referrerCode ? ` Give the referrer their code directly: ${res.referrerCode}` : ''}`)
          } else {
            this.notifySuccess(this.convertedSuccessText(res))
          }
        } else if (newStatus === 'not_interested') {
          this.notifySuccess('Marked as not interested')
        } else {
          this.notifySuccess('Lead status updated')
        }
        this.loadReferrals()
        if (this.lookupResult && this.lookupResult.referral && this.lookupResult.referral.referralId === referral.referralId) {
          this.runLookup()
        }
      }).catch((e) => {
        this.processingId = null
        this.notifyError(this.referralErrorText(e, 'Could not update the referral'))
        this.loadReferrals()
      })
    },

    confirmReferral (referral, action) {
      if (this.processingId) return // guard against double-clicks across rows
      // group confirm (e.g. "Confirm this group" in the code console) shows the fill state
      // first — partial groups CAN be confirmed, but staff should know what they're redeeming
      if (action === 'confirm' && referral.origin === 'group') {
        if (!window.confirm(this.groupConfirmPrompt(referral))) return
      }
      this.processingId = referral.referralId
      this.processingAction = action
      this.$store.dispatch('dash/CONFIRM_REFERRAL', {
        referralId: referral.referralId,
        action
      }).then((res) => {
        if (action === 'reject') {
          this.notifySuccess('Referral rejected')
        } else if (res && res.rewardReason === 'emailfailed') {
          this.notifyWarning(`Confirmed, but the reward email could not be sent.${res.referrerCode ? ` Give the referrer their code directly: ${res.referrerCode}` : ''}`)
        } else if (res && res.rewarded) {
          this.notifySuccess(res.origin === 'group' ? 'Group confirmed — referrer reward emailed' : 'Confirmed — referrer reward emailed')
        } else {
          this.notifySuccess(this.convertedSuccessText(res))
        }
        this.processingId = null
        this.processingAction = null
        this.loadReferrals()
        if (this.lookupResult && this.lookupResult.referral && this.lookupResult.referral.referralId === referral.referralId) {
          this.runLookup()
        }
      }).catch((e) => {
        this.processingId = null
        this.processingAction = null
        this.notifyError(this.referralErrorText(e, 'Could not update the referral'))
      })
    }
  },
  created () {
    const initialFetch = this.loadReferrals()

    // ?code=XYZ deep link — staff scan a QR on a customer's reward email/phone
    // that opens /dash/referrals?code=ABC123; auto-run the lookup once loaded.
    const deepLinkCode = (this.$route.query.code || '').toString().trim().toUpperCase()
    if (deepLinkCode) {
      this.lookupCode = deepLinkCode
      initialFetch.then(() => {
        this.runLookup()
      })
    }
  }
}
</script>

<style scoped>
.btn-spin {
  display: inline-block;
  width: 0.9em;
  height: 0.9em;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
}
.leadstatus-select {
  padding: 4px 8px;
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
  color: #333;
  cursor: pointer;
}
.leadstatus-select:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
